import './box-builder.css'
import BoxState, { EVENT_TYPES } from '../../scripts/lib/box-state.js'
import { isSpecialProduct, getProductCollections } from '../../scripts/lib/product-helpers.js'
import { DEFAULT_MEAL_PLAN_URL } from '../../scripts/lib/constants.js'
import { getCartState } from '../../scripts/lib/cart-helpers.js'
import DiscountService from '../../scripts/lib/discount-service.js'
import '../recipe-card/recipe-card.js'
import '../box-sampler-view/box-sampler-view.js'
import '../box-summary-view/box-summary-view.js'

const SAMPLER_STEPS = {
  STARTED: 'started',
  RECIPE: 'recipe',
  REVIEW: 'review'
}

class BoxBuilder extends HTMLElement {
  #boxSummary
  #cardEls = []
  #boxState


  constructor() {
    super()

    this.#boxState = BoxState.getInstance()
    this.handleRecipeCardQuantityChange = this.handleRecipeCardQuantityChange.bind(this)
    this.handleFrequencyChange = this.handleFrequencyChange.bind(this)
    this.handleBoxItemsChange = this.handleBoxItemsChange.bind(this)
    this.handleActiveViewChange = this.handleActiveViewChange.bind(this)
  }

  handleBoxItemsChange() {
    this.#updateUIFromBoxState()
  }

  handleActiveViewChange(newView) {
    this.dataset.view = newView

    if (newView === 'summary') {
      const summaryView = this.querySelector('box-summary-view')
      if (summaryView) {
        summaryView.boxItems = this.#boxState.boxItems
        summaryView.selections = this.#boxState.samplerSelections || []
      }
    } else if (newView === 'sampler') {
      const samplerView = this.querySelector('box-sampler-view')
      if (samplerView) {
        samplerView.render()
      }
    }
  }

  #redirectToMealPlanIfInvalidWeights() {
    if (!this.#boxState.hasValidPetWeights()) {
      const mealPlanUrl = this.dataset.mealPlanUrl || DEFAULT_MEAL_PLAN_URL
      window.location.href = mealPlanUrl
      return true
    }
    return false
  }

  #updateUIFromBoxState() {
    const boxItems = this.#boxState.boxItems

    this.#cardEls.forEach(card => {
      const variantId = card.dataset.variantId
      if (variantId && boxItems[variantId]) {
        const newQuantity = boxItems[variantId].quantity
        if (parseInt(card.dataset.quantity) !== newQuantity) {
          card.dataset.quantity = newQuantity.toString()
        }
      }
    })

    this.#updateBoxSummary()
  }

  async #importFromShopifyCartIfNeeded() {
    const existingItems = this.#boxState.boxItems
    const hasExistingSelections = Object.values(existingItems).some(
      item => (typeof item === 'object' ? item.quantity : item) > 0
    )
    if (hasExistingSelections) return

    const boxProducts = window.VIVA?.boxProducts || []
    if (boxProducts.length === 0) return

    const boxVariantIds = new Set(boxProducts.map(p => p.variantId.toString()))

    try {
      const cartState = await getCartState()
      if (cartState.items.length === 0) return

      const matchingItems = cartState.items.filter(
        item => boxVariantIds.has(item.variantId.toString())
      )
      if (matchingItems.length === 0) return

      const importedItems = { ...this.#boxState.boxItems }
      matchingItems.forEach(item => {
        importedItems[item.variantId.toString()] = { quantity: item.quantity }
      })
      this.#boxState.boxItems = importedItems
    } catch (error) {
      console.error('[BoxBuilder] Failed to import from Shopify cart:', error)
    }
  }

  async connectedCallback() {
    this.setupEventListeners();
    this.#boxState.subscribe(EVENT_TYPES.BOX_ITEMS_CHANGE, this.handleBoxItemsChange)
    this.#boxState.subscribe(EVENT_TYPES.ACTIVE_VIEW_CHANGE, this.handleActiveViewChange)

    await this.#importFromShopifyCartIfNeeded()
    this.setupCards()

    const boxItems = this.#boxState.boxItems
    const hasSelectedItems = Object.values(boxItems).some(item => item.quantity > 0)
    this.setAttribute('data-mobile-mode', hasSelectedItems ? 'review' : 'edit')
    
    this.#boxState.subscribe(EVENT_TYPES.PET_PROFILES_CHANGE, () => {
      this.#boxState.calculateRecommendations(false)

      const trialCart = window.VIVA?.trialCart?.enabled ?? false
      if (trialCart && this.#boxState.activeView === 'sampler') {
        this.#redirectToMealPlanIfInvalidWeights()
      }
    })

    const trialCartEnabled = window.VIVA?.trialCart?.enabled ?? false
    const samplerStep = this.#boxState.samplerStep

    if (trialCartEnabled && samplerStep) {
      if (this.#redirectToMealPlanIfInvalidWeights()) return

      if (samplerStep === SAMPLER_STEPS.STARTED) {
        this.#boxState.setSamplerStep(SAMPLER_STEPS.RECIPE)
        this.#boxState.activeView = 'sampler'
        this.dataset.view = this.#boxState.activeView
      } else if (samplerStep === SAMPLER_STEPS.RECIPE) {
        this.#boxState.activeView = 'sampler'
      } else if (samplerStep === SAMPLER_STEPS.REVIEW) {
        this.#boxState.activeView = 'summary'
      }
    } else {
      this.dataset.view = this.#boxState.activeView
    }

    const urlParams = new URLSearchParams(window.location.search)
    if (trialCartEnabled && urlParams.get('sampler') === '1') {
      window.history.replaceState({}, '', window.location.pathname)
      this.#handleSamplerUrlParam()
    }

    this.#boxSummary = this.querySelector('box-summary')
    this.#updateBoxSummary()

    this.#setInitialToggle()
    
    if (this.#boxState.petProfiles.length > 0) {
      this.#boxState.calculateRecommendations(false)
    }

    this.hidden = false

    this.#initializeDiscountCodes()
  }

  async #initializeDiscountCodes() {
    try {
      const codes = window.VIVA?.cartDiscountCodes || []
      this.#boxState.setCartDiscountCodes(codes)

      const shop = window.Shopify?.shop || window.VIVA?.shopName
      if (!shop) return

      const persistedInfo = this.#boxState.samplerDiscountInfo
      if (persistedInfo?.code) {
        const discountService = DiscountService.getInstance()
        const validationResult = await discountService.checkSamplerDiscount(persistedInfo.code, shop)

        if (validationResult?.isSampler) {
          if (validationResult.percentageOff !== persistedInfo.percentageOff) {
            this.#boxState.setSamplerDiscountInfo(validationResult)
          }
          return
        }

        this.#boxState.setSamplerDiscountInfo(null)
      }

      if (codes.length === 0) return

      const discountService = DiscountService.getInstance()
      for (const code of codes) {
        const info = await discountService.checkSamplerDiscount(code, shop)
        if (info?.isSampler) {
          this.#boxState.setSamplerDiscountInfo(info)
          break
        }
      }
    } catch (error) {
      console.error('[BoxBuilder] Discount initialization failed:', error)
    }
  }

  disconnectedCallback() {
    this.removeEventListeners()
    this.#boxState.unsubscribe(EVENT_TYPES.BOX_ITEMS_CHANGE, this.handleBoxItemsChange)
    this.#boxState.unsubscribe(EVENT_TYPES.ACTIVE_VIEW_CHANGE, this.handleActiveViewChange)
  }

  #handleSamplerUrlParam() {
    const currentEmail = this.#boxState.getCurrentEmail()

    if (!currentEmail) {
      const samplerModal = this.querySelector('sampler-modal')
      if (samplerModal?.show) {
        samplerModal.removeAttribute('hidden')
        samplerModal.show()
      }
      return
    }

    this.#boxState.setSamplerStep(SAMPLER_STEPS.STARTED)

    if (this.#redirectToMealPlanIfInvalidWeights()) return

    this.#boxState.setSamplerStep(SAMPLER_STEPS.RECIPE)
    this.#boxState.activeView = 'sampler'
  }

  setupCards() {
    const boxProducts = window.VIVA?.boxProducts || []

    if (boxProducts.length === 0) {
      console.warn('box-builder: No products found in window.VIVA.boxProducts')
      return
    }

    const sortedProducts = this.#sortProductsByTexture(boxProducts)

    this.#renderRecipeCards(sortedProducts)

    this.#cardEls = Array.from(this.querySelectorAll('.box-product-list__grid recipe-card'))

    const savedBoxItems = this.#boxState.boxItems

    const newBoxItems = {}
    sortedProducts.forEach((product) => {
      const variantId = product.variantId.toString()

      const quantity = typeof savedBoxItems[variantId] === 'object' && savedBoxItems[variantId].quantity
        ? savedBoxItems[variantId].quantity
        : (typeof savedBoxItems[variantId] === 'number' ? savedBoxItems[variantId] : 0)

      newBoxItems[variantId] = {
        ...product,
        quantity
      }

      const cardEl = this.#cardEls.find(el => el.dataset.variantId === variantId)
      if (cardEl && quantity > 0) {
        cardEl.dataset.quantity = quantity.toString()
      }
    })

    this.#boxState.boxItems = newBoxItems
  }

  #sortProductsByTexture(products) {
    const specials = []
    const badged = []
    const ground = []
    const chunked = []

    products.forEach(product => {
      if (isSpecialProduct(product)) {
        specials.push(product)
        return
      }

      if (product.customBadgeLabel) {
        badged.push(product)
        return
      }

      const texture = product.variantOption1?.toLowerCase() || ''
      if (texture.includes('ground')) {
        ground.push(product)
      } else if (texture.includes('chunked')) {
        chunked.push(product)
      }
    })

    return [...specials, ...badged, ...ground, ...chunked]
  }

  #renderRecipeCards(products) {
    const grid = this.querySelector('.box-product-list__grid')
    if (!grid) return

    products.forEach(product => {
      const card = document.createElement('recipe-card')
      card.className = 'recipe-card v:flex v:flex-col v:gap-3'

      card.dataset.variantId = product.variantId
      card.dataset.productTitle = product.productTitle
      card.dataset.productUrl = product.productUrl
      card.dataset.variantOption1 = product.variantOption1
      card.dataset.price = product.price
      card.dataset.compareAtPrice = product.compareAtPrice
      card.dataset.available = product.available
      card.dataset.restockDate = product.restockDate || ''
      card.dataset.poundsPerPackage = product.poundsPerPackage
      card.dataset.featuredImageUrl = product.featuredImageUrl || ''
      card.dataset.featuredImageWidth = product.featuredImageWidth || ''
      card.dataset.featuredImageAlt = product.featuredImageAlt || ''
      card.dataset.secondaryImageUrl = product.secondaryImageUrl || ''
      card.dataset.secondaryImageWidth = product.secondaryImageWidth || ''
      if (product.imageBgColor) {
        card.dataset.imageBgColor = product.imageBgColor
      }
      if (product.customBadgeLabel) {
        card.dataset.customBadgeLabel = product.customBadgeLabel
        card.dataset.customBadgeBgColor = product.customBadgeBgColor || ''
        card.dataset.customBadgeTextColor = product.customBadgeTextColor || ''
      }
      card.dataset.isSpecial = isSpecialProduct(product) ? 'true' : 'false'
      card.dataset.showActions = 'true'
      card.dataset.showSpecialBadge = 'true'
      card.dataset.cardPerRowMobile = '2'
      card.dataset.cardPerRowDesktop = '3'
      card.dataset.quantity = '0'
      card.dataset.state = 'idle'

      const collections = getProductCollections(product)
      const toggleType = collections.length > 0
        ? '|' + collections.map(c => c.handle).join('|') + '|'
        : ''
      card.setAttribute('data-toggle-type', toggleType)

      grid.appendChild(card)
    })
  }

  #updateBoxSummary() {
    if (!this.#boxSummary) {
      return
    }

    const boxItems = this.#boxState.boxItems
    const itemsWithQuantity = Object.entries(boxItems)
      .filter(([_, item]) => item.quantity > 0)
      .map(([variantId, item]) => ({
        variantId,
        ...item
      }))

    this.#boxSummary.boxItems = itemsWithQuantity
  }

  #getToggleValueForPetProfiles(petProfiles) {
    if (!petProfiles || petProfiles.length === 0) return 'all'

    const hasCats = petProfiles.some(pet => pet.species === 'cat')
    const hasDogs = petProfiles.some(pet => pet.species === 'dog')

    if (hasCats && !hasDogs) return 'for-cats'
    if (hasDogs && !hasCats) return 'for-dogs'
    return 'all'
  }

  async #setInitialToggle() {
    const urlParams = new URLSearchParams(window.location.search)
    const tabParam = urlParams.get('tab')

    const togglesComponent = this.querySelector('v-toggles')
    if (!togglesComponent) return

    if (tabParam) {
      if (!togglesComponent.setValue(tabParam)) {
        console.warn(`[box-builder] Invalid tab parameter: ${tabParam}, falling back to 'all'`)
        togglesComponent.setValue('all')
      }
      return
    }

    await this.#waitForPetProfiles()

    const toggleValue = this.#getToggleValueForPetProfiles(this.#boxState.petProfiles)
    togglesComponent.setValue(toggleValue)
  }

  #waitForPetProfiles() {
    const petProfiles = this.#boxState.petProfiles

    if (petProfiles !== null && petProfiles !== undefined) {
      return Promise.resolve()
    }

    return new Promise((resolve) => {
      const handler = () => {
        this.#boxState.unsubscribe(EVENT_TYPES.PET_PROFILES_CHANGE, handler)
        resolve()
      }
      this.#boxState.subscribe(EVENT_TYPES.PET_PROFILES_CHANGE, handler)
    })
  }

  setupEventListeners() {
    this.addEventListener('recipe-card:quantity-change', this.handleRecipeCardQuantityChange)
    this.addEventListener('box-summary:frequency-change', this.handleFrequencyChange)

    this.addEventListener('summary:back', () => {
      this.#boxState.activeView = 'sampler'
      window.scrollTo(0, 0)
    })

    this.addEventListener('click', (e) => {
      if (e.target.matches('[data-mobile-review-btn]')) {
        this.setAttribute('data-mobile-mode', 'review')
        window.scrollTo(0, 0)
      } else if (e.target.matches('[data-mobile-edit-btn]')) {
        this.setAttribute('data-mobile-mode', 'edit')
        window.scrollTo(0, 0)
      } else if (e.target.matches('[data-sampler-back-btn]')) {
        e.preventDefault()
        this.#boxState.clearSamplerStep()
        this.#boxState.activeView = 'builder'
        window.scrollTo(0, 0)
      } else if (e.target.matches('[data-summary-back-btn], [data-edit-recipes-btn]')) {
        e.preventDefault()
        this.#boxState.setSamplerStep('recipe')
        this.#boxState.activeView = 'sampler'
        window.scrollTo(0, 0)
      }
    })
  }

  removeEventListeners () {
    this.removeEventListener('recipe-card:quantity-change', this.handleRecipeCardQuantityChange)
    this.removeEventListener('box-summary:frequency-change', this.handleFrequencyChange)
  }

  handleRecipeCardQuantityChange(event) {
    const { variantId, newQuantity } = event.detail

    if (!variantId) return

    this.#boxState.updateBoxItemQuantity(variantId, newQuantity)
  }

  handleFrequencyChange(event) {
    const { frequency } = event.detail

    if (!frequency) return

    this.#boxState.frequency = frequency
  }
}

customElements.define('box-builder', BoxBuilder)